import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, FileSpreadsheet, FileText, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { LosMark } from "@/components/Logo";
import { SheetGrid } from "@/components/canvas/SheetGrid";
import { BlobView, TextView, downloadOriginal as download } from "@/components/canvas/DocViews";
import { DOC_ROLE } from "@/lib/sk";

const downloadOriginal = (caseId, doc) => download(caseId, doc, (m) => toast.error(m));

/**
 * /p/:id/kilde/:docId – the original a value came from, opened at the exact spot (PDF page, sheet row/cell).
 * Stays inside the logged-in workspace; nothing is shared or made public.
 */
export default function SourcePage() {
  const { id, docId } = useParams();
  const [q] = useSearchParams();
  const page = Number(q.get("page")) || null;
  const sheet = q.get("sheet") || null;
  const row = q.get("row") || null;
  const cell = q.get("cell") || null;
  const queries = useMemo(() => q.getAll("q").filter(Boolean), [q]);
  const where = useMemo(() => ({ page, pages: (q.get("pages") || "").split(",").map(Number).filter(Boolean), q: queries }), [page, q, queries]);
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/projects/${id}/documents/${docId}`).then((r) => setMeta(r.data)).catch((e) => setErr(errMsg(e)));
  }, [id, docId]);
  useEffect(() => { if (meta?.document?.name) document.title = `${meta.document.name} – LOS Marine`; }, [meta]);

  const doc = meta?.document;
  const spot = page ? `side ${page}${queries.length ? " · kildetekst markert" : ""}` : cell ? `ark «${sheet || doc?.name}» · celle ${cell}` : row ? `ark «${sheet || "?"}» · rad ${row}` : null;
  const Icon = doc?.kind === "xlsx" ? FileSpreadsheet : doc?.kind === "image" ? ImageIcon : FileText;
  return (
    <div className="flex h-screen flex-col bg-background" data-testid="source-page" data-doc-kind={doc?.kind || undefined}>
      <header className="flex h-12 shrink-0 items-center gap-3 border-b border-black/[0.06] bg-white px-3 sm:px-4">
        <Link to={`/p/${id}`} className="inline-flex h-8 items-center gap-1.5 rounded-full px-2.5 text-[12.5px] text-foreground/70 hover:bg-black/[0.05] hover:text-foreground" data-testid="source-back"><ArrowLeft className="h-3.5 w-3.5" /> Saken</Link>
        <LosMark className="hidden h-5 w-5 sm:block" />
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Icon className="h-4 w-4 shrink-0 text-primary" />
          <span className="truncate text-[13.5px] font-medium" title={doc?.name} data-testid="source-doc-name">{doc?.name || "…"}</span>
          {doc?.role ? <span className="hidden shrink-0 text-[11.5px] text-muted-foreground sm:inline">{DOC_ROLE[doc.role]?.label || doc.role}</span> : null}
          {spot ? <span className="shrink-0 rounded-md bg-[hsl(var(--brand-soft))] px-1.5 py-0.5 font-mono text-[11px] text-[hsl(var(--navy))]" data-testid="source-where">{spot}</span> : null}
        </div>
        {meta?.case ? <span className="hidden truncate text-[12px] text-muted-foreground md:inline" data-testid="source-case">#{meta.case.number} · {meta.case.name}</span> : null}
        {doc ? <button type="button" onClick={() => downloadOriginal(id, doc)} className="inline-flex h-8 items-center gap-1.5 rounded-full border border-black/[0.1] bg-white px-2.5 text-[12.5px] font-medium hover:border-black/[0.25]" data-testid="source-download"><Download className="h-3.5 w-3.5" /> Last ned original</button> : null}
      </header>
      <main className="min-h-0 flex-1">
        {err ? <p className="p-6 text-[13px] text-[hsl(var(--v-blocked))]" data-testid="source-page-error">{err}</p>
          : !doc ? <div className="flex h-full items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
            : doc.kind === "xlsx" ? <div className="h-full overflow-auto p-4 sm:p-6"><SheetGrid caseId={id} docId={doc.id} sheet={sheet} row={row} cell={cell} maxHeight="calc(100vh - 140px)" /></div>
              : doc.kind === "pdf" || doc.kind === "image" ? <BlobView caseId={id} doc={doc} page={page} where={where} />
                : <TextView caseId={id} doc={doc} />}
      </main>
    </div>
  );
}
